import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const TYPES = ["Ring", "Necklace", "Bracelet", "Earrings", "Bridal Set", "Other"];
const METALS = ["", "9K Gold", "14K Gold", "18K Gold", "Platinum", "Silver"];
const GEMS = ["", "Diamond", "Lab Diamond", "Emerald", "Ruby", "Sapphire", "Pearl", "None"];
const OCCASIONS = ["", "Wedding", "Engagement", "Anniversary", "Birthday", "Vacation", "Gift"];
const BUDGETS = ["Under ₹10,000", "₹20,000 – ₹50,000", "₹ ₹50,000-₹5,00,000+"];

export default function MakeYourOwn() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", jewelry_type: "Ring",
    metal: "", gemstone: "", occasion: "", budget: BUDGETS[1],
    details: "", reference_image: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/consultations", form);
      toast.success("Consultation requested — we'll be in touch within 24 hours.");
      setDone(true);
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Failed to submit");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="make-your-own-page">
      <section className="relative h-[60vh] overflow-hidden">
        <img src="https://images.pexels.com/photos/972937/pexels-photo-972937.jpeg" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.2), rgba(0,0,0,0.6))" }} />
        <div className="relative ja-container h-full flex items-end pb-20 text-white">
          <div className="max-w-2xl">
            <p className="text-[11px] tracking-[0.4em] uppercase" style={{ color: "var(--ja-champagne)" }}>Atelier Service</p>
            <h1 className="ja-serif text-5xl md:text-7xl mt-6 leading-[0.95]">Make Your Own.</h1>
            <p className="mt-4 max-w-md font-light">From sketch to setting — design a one-of-one piece with our master jewellers over a private consultation.</p>
          </div>
        </div>
      </section>

      <section className="ja-container ja-section">
        <div className="grid md:grid-cols-3 gap-12 mb-20">
          {[
            ["01", "Share Your Vision", "Tell us the occasion, the muse and the budget. Reference images welcome."],
            ["02", "Sketch & Render", "Our designers send three hand-drawn concepts within 5 days."],
            ["03", "Crafted by Hand", "Approved design is cast, set and polished by our master jewellers in 4–6 weeks."],
          ].map(([n, t, d]) => (
            <div key={n} className="border-t pt-8" style={{ borderColor: "var(--ja-border)" }}>
              <p className="ja-serif text-5xl" style={{ color: "var(--ja-gold)" }}>{n}</p>
              <p className="ja-serif text-2xl mt-6">{t}</p>
              <p className="mt-3 text-sm" style={{ color: "var(--ja-muted)" }}>{d}</p>
            </div>
          ))}
        </div>

        {done ? (
          <div className="text-center py-16 border" style={{ borderColor: "var(--ja-border)", background: "var(--ja-ivory)" }}>
            <p className="ja-overline">Received</p>
            <h2 className="ja-serif text-4xl mt-3">Your request is with our atelier.</h2>
            <p className="mt-4 text-sm" style={{ color: "var(--ja-muted)" }}>A senior designer will contact you within 24 hours.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="max-w-3xl mx-auto">
            <p className="ja-overline mb-6 text-center">Begin Your Commission</p>
            <div className="grid sm:grid-cols-2 gap-6">
              <input required placeholder="Full Name" className="ja-input" value={form.name} onChange={onChange("name")} data-testid="myo-name" />
              <input required type="email" placeholder="Email" className="ja-input" value={form.email} onChange={onChange("email")} data-testid="myo-email" />
              <input required placeholder="Phone (with country code)" className="ja-input sm:col-span-2" value={form.phone} onChange={onChange("phone")} data-testid="myo-phone" />
              <select className="ja-input" value={form.jewelry_type} onChange={onChange("jewelry_type")} data-testid="myo-type">
                {TYPES.map((t) => <option key={t}>{t}</option>)}
              </select>
              <select className="ja-input" value={form.occasion} onChange={onChange("occasion")} data-testid="myo-occasion">
                <option value="">Occasion</option>{OCCASIONS.slice(1).map((o) => <option key={o}>{o}</option>)}
              </select>
              <select className="ja-input" value={form.metal} onChange={onChange("metal")} data-testid="myo-metal">
                <option value="">Preferred Metal</option>{METALS.slice(1).map((m) => <option key={m}>{m}</option>)}
              </select>
              <select className="ja-input" value={form.gemstone} onChange={onChange("gemstone")} data-testid="myo-gemstone">
                <option value="">Preferred Gemstone</option>{GEMS.slice(1).map((g) => <option key={g}>{g}</option>)}
              </select>
              <select className="ja-input sm:col-span-2" value={form.budget} onChange={onChange("budget")} data-testid="myo-budget">
                {BUDGETS.map((b) => <option key={b}>{b}</option>)}
              </select>
              <input placeholder="Reference image URL (optional)" className="ja-input sm:col-span-2" value={form.reference_image} onChange={onChange("reference_image")} data-testid="myo-ref" />
              <textarea required rows={5} placeholder="Describe your vision — silhouette, story, inspiration…" className="ja-input sm:col-span-2 resize-none" value={form.details} onChange={onChange("details")} data-testid="myo-details" />
            </div>
            <div className="mt-10 text-center">
              <button type="submit" disabled={busy} className="ja-btn" data-testid="myo-submit">{busy ? "Submitting…" : "Request Consultation"}</button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
